import { useMemo, type ReactNode } from 'react';
import { BarChart, DonutChart } from '@tremor/react';
import {
  Award,
  CalendarDays,
  IndianRupee,
  Layers,
  MapPin,
  Package,
  Percent,
  Printer,
  ReceiptText,
  ShoppingBag,
  Store,
  TrendingDown,
  Trophy,
  Utensils,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { KpiTile } from '@/components/charts/KpiTile';
import { DeltaBadge } from '@/components/charts/DeltaBadge';
import { EmptyState } from '@/components/charts/EmptyState';
import { MotionCard } from '@/components/motion/MotionCard';
import { Button } from '@/components/ui/button';
import { useDashboard } from '@/hooks/useDashboardContext';
import { usePosReview, usePosMenu } from '@/hooks/usePosReview';
import { computeMetric, type Metric } from '@/lib/periods';
import {
  fmtISTRange,
  formatINRCompact,
  formatNumberCompact,
  formatPercent,
} from '@/lib/format';
import { cn } from '@/lib/utils';

/** Numbered, deck-style section wrapper so teams can walk the review page-by-page. */
function ReviewSection({
  step,
  title,
  description,
  children,
}: {
  step: number;
  title: string;
  description?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="scroll-mt-20 border-t border-border/40 pt-8 first:border-t-0 first:pt-0">
      <div className="mb-4 flex items-start gap-3">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background">
          {step}
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          {description && (
            <p className="mt-0.5 text-sm text-muted-foreground text-pretty">{description}</p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

function HighlightCard({
  label,
  icon,
  value,
  sub,
  metric,
  tone,
}: {
  label: string;
  icon: ReactNode;
  value: string;
  sub?: string;
  metric?: Metric;
  tone?: 'positive' | 'negative';
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 pb-2">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
        <span
          className={cn(
            'text-muted-foreground/70',
            tone === 'positive' && 'text-emerald-500',
            tone === 'negative' && 'text-rose-500',
          )}
        >
          {icon}
        </span>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="truncate text-xl font-semibold tracking-tight">{value}</div>
        {sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}
        {metric && (
          <div className="pt-1">
            <DeltaBadge deltaPct={metric.deltaPct} comparable={metric.comparable} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const CHANNEL_COLOR: Record<string, string> = {
  'Dine In': 'bg-emerald-500',
  'Pick Up': 'bg-sky-500',
  Delivery: 'bg-indigo-500',
  Other: 'bg-muted-foreground',
};

export function BusinessReviewPage() {
  const { period } = useDashboard();
  const review = usePosReview({ period });
  const menu = usePosMenu({ period, limit: 8 });
  const menuItems = useMemo(() => menu.data?.items ?? [], [menu.data]);
  const categories = useMemo(() => menu.data?.categories ?? [], [menu.data]);

  const data = review.data;
  const outlets = useMemo(() => data?.outlets ?? [], [data]);
  const best = outlets[0];
  const lowest = outlets.length > 1 ? outlets[outlets.length - 1] : undefined;

  const rankingChart = useMemo(
    () => outlets.map((o) => ({ name: o.name, 'Net sales': o.net })),
    [outlets],
  );

  const providerChart = useMemo(
    () =>
      (data?.perOutletSource ?? []).map((o) => ({
        name: o.name,
        Zomato: o.zomato,
        Swiggy: o.swiggy,
      })),
    [data],
  );

  const splitDonut = useMemo(() => {
    if (!data || data.aggregator.total <= 0) return [];
    return [
      { name: 'Zomato', value: data.aggregator.zomato },
      { name: 'Swiggy', value: data.aggregator.swiggy },
    ];
  }, [data]);

  // Dine-in vs online from the channel mix.
  const dineIn = data?.channelMix.find((c) => c.key === 'Dine In');
  const dineInPct = dineIn?.sharePct ?? null;
  const onlinePct = dineInPct != null ? 100 - dineInPct : null;

  const insights = useMemo(() => {
    if (!data) return [];
    const out: string[] = [];
    const b = data.brand;
    out.push(`Net sales reached ${formatINRCompact(b.net)} across ${outlets.length} outlets (gross ${formatINRCompact(b.gross)}).`);
    if (best) out.push(`${best.name} led the network at ${formatINRCompact(best.net)} net sales.`);
    if (outlets.length >= 3) {
      const top3 = outlets.slice(0, 3).reduce((a, o) => a + o.net, 0);
      const total = outlets.reduce((a, o) => a + o.net, 0);
      if (total > 0) out.push(`Top 3 outlets contributed ${((top3 / total) * 100).toFixed(0)}% of net sales.`);
    }
    if (onlinePct != null) out.push(`Channel split held at ${onlinePct.toFixed(0)}% online : ${(100 - onlinePct).toFixed(0)}% dine-in.`);
    if (data.aggregator.total > 0)
      out.push(`Aggregator split: Zomato ${data.aggregator.zomatoPct.toFixed(0)} : ${(100 - data.aggregator.zomatoPct).toFixed(0)} Swiggy.`);
    const firstWeek = data.weekly[0];
    const lastWeek = data.weekly[data.weekly.length - 1];
    if (data.weekly.length >= 2 && firstWeek && lastWeek && firstWeek.net > 0) {
      out.push(
        `Net sales moved ${formatPercent(((lastWeek.net - firstWeek.net) / firstWeek.net) * 100, { signed: true, digits: 1 })} from the first to the last week.`,
      );
    }
    if (menuItems[0]) out.push(`${menuItems[0].name} was the top-selling product by revenue.`);
    if (categories[0]) out.push(`${categories[0].category} was the top category at ${categories[0].sharePct.toFixed(0)}% of menu revenue.`);
    if (b.discountPct > 0) out.push(`Discounts ran at ${formatPercent(b.discountPct, { digits: 0 })} of sales; tax collected ${formatINRCompact(b.tax)}.`);
    if (lowest && lowest !== best) out.push(`${lowest.name} was the lowest performer — a demand-generation focus.`);
    return out;
  }, [data, outlets, best, lowest, onlinePct, menuItems, categories]);

  const rangeLabel = data
    ? `${fmtISTRange(review.current.start, review.current.end)} vs ${fmtISTRange(review.previous.start, review.previous.end)}`
    : review.current.label;

  const loading = review.isLoading;
  const noData = !loading && outlets.length === 0;

  return (
    <div className="space-y-2 print:space-y-6">
      <SectionHeader
        title="Business Review"
        description={`Network-wide · Petpooja POS · ${rangeLabel}`}
        actions={
          <Button variant="outline" size="sm" className="print:hidden" onClick={() => window.print()}>
            <Printer className="mr-1.5 h-3.5 w-3.5" /> Print / PDF
          </Button>
        }
      />

      {review.isError ? (
        <EmptyState
          title="Could not load POS data"
          description="Check that VITE_SUPABASE_MARKETING_URL / ANON_KEY are set — the review reads Petpooja sales from the Inventory project."
        />
      ) : noData ? (
        <EmptyState
          icon={<Utensils className="h-5 w-5" />}
          title="No POS sales for this period"
          description="No Petpooja sales found for the selected dates. Try a different period (e.g. This month)."
        />
      ) : (
        <div className="space-y-8">
          {/* 1 — Network performance */}
          <ReviewSection
            step={1}
            title="Network performance"
            description="Net sales, gross, discounts and tax for the period, vs the previous period."
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <KpiTile
                label="Net sales"
                icon={<IndianRupee className="h-3.5 w-3.5" />}
                metric={data?.brand.netMetric}
                loading={loading}
                format={formatINRCompact}
                comparisonRange={{ start: review.previous.start, end: review.previous.end }}
              />
              <KpiTile
                label="Gross sales"
                icon={<IndianRupee className="h-3.5 w-3.5" />}
                metric={data?.brand.grossMetric}
                loading={loading}
                format={formatINRCompact}
                delay={0.05}
              />
              <KpiTile
                label="Bills"
                icon={<ShoppingBag className="h-3.5 w-3.5" />}
                metric={data?.brand.billsMetric}
                loading={loading}
                format={formatNumberCompact}
                delay={0.1}
              />
              <KpiTile
                label="Avg bill"
                icon={<ReceiptText className="h-3.5 w-3.5" />}
                metric={data?.brand.aovMetric}
                loading={loading}
                format={formatINRCompact}
                delay={0.15}
              />
              <KpiTile
                label="Discount"
                icon={<Percent className="h-3.5 w-3.5" />}
                metric={data ? computeMetric(data.brand.discountPct, data.brand.discountPct) : undefined}
                loading={loading}
                format={(v) => formatPercent(v, { digits: 1 })}
                delay={0.2}
              />
              <KpiTile
                label="Tax collected"
                icon={<ReceiptText className="h-3.5 w-3.5" />}
                metric={data ? computeMetric(data.brand.tax, data.brand.tax) : undefined}
                loading={loading}
                format={formatINRCompact}
                delay={0.25}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <HighlightCard
                label="Best performing outlet"
                icon={<Trophy className="h-4 w-4" />}
                tone="positive"
                value={best ? best.name : loading ? '…' : '—'}
                sub={best ? `${formatINRCompact(best.net)} net · ${best.city}` : undefined}
                metric={best?.netMetric}
              />
              <HighlightCard
                label="Needs attention"
                icon={<Store className="h-4 w-4" />}
                tone="negative"
                value={lowest ? lowest.name : loading ? '…' : '—'}
                sub={lowest ? `${formatINRCompact(lowest.net)} net · ${lowest.city}` : undefined}
                metric={lowest?.netMetric}
              />
            </div>
          </ReviewSection>

          {/* 2 — Outlet ranking */}
          <ReviewSection step={2} title="Outlet ranking" description="Net sales by outlet, highest to lowest.">
            <MotionCard>
              <Card>
                <CardHeader>
                  <CardTitle>Net sales by outlet</CardTitle>
                </CardHeader>
                <CardContent>
                  {rankingChart.length > 0 ? (
                    <BarChart
                      data={rankingChart}
                      index="name"
                      categories={['Net sales']}
                      colors={['emerald']}
                      layout="vertical"
                      valueFormatter={formatINRCompact}
                      showLegend={false}
                      showGridLines={false}
                      yAxisWidth={120}
                      className="h-80"
                    />
                  ) : (
                    <EmptyState title={loading ? 'Loading…' : 'No outlet sales'} />
                  )}
                </CardContent>
              </Card>
            </MotionCard>
          </ReviewSection>

          {/* 3 — Aggregator split */}
          <ReviewSection
            step={3}
            title="Aggregator split — Zomato vs Swiggy"
            description="Online revenue share across the two aggregators."
          >
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <MotionCard>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>Network split</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {splitDonut.length > 0 && data ? (
                      <>
                        <DonutChart
                          data={splitDonut}
                          category="value"
                          index="name"
                          colors={['rose', 'orange']}
                          valueFormatter={formatINRCompact}
                          className="h-44"
                        />
                        <div className="mt-4 space-y-1.5 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full bg-rose-500" /> Zomato
                            </span>
                            <span className="font-mono tabular-nums">
                              {formatINRCompact(data.aggregator.zomato)} ·{' '}
                              {formatPercent(data.aggregator.zomatoPct, { digits: 0 })}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full bg-orange-500" /> Swiggy
                            </span>
                            <span className="font-mono tabular-nums">
                              {formatINRCompact(data.aggregator.swiggy)} ·{' '}
                              {formatPercent(100 - data.aggregator.zomatoPct, { digits: 0 })}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <EmptyState title={loading ? 'Loading…' : 'No aggregator sales'} />
                    )}
                  </CardContent>
                </Card>
              </MotionCard>

              <MotionCard className="lg:col-span-2" delay={0.05}>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle>By outlet</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {providerChart.length > 0 ? (
                      <BarChart
                        data={providerChart}
                        index="name"
                        categories={['Zomato', 'Swiggy']}
                        colors={['rose', 'orange']}
                        valueFormatter={formatINRCompact}
                        showGridLines={false}
                        stack
                        className="h-80"
                      />
                    ) : (
                      <EmptyState title={loading ? 'Loading…' : 'No per-outlet aggregator data'} />
                    )}
                  </CardContent>
                </Card>
              </MotionCard>
            </div>
          </ReviewSection>

          {/* 4 — Channel mix */}
          <ReviewSection
            step={4}
            title="Channel mix — Dine-in vs Online"
            description="Revenue split across dine-in, pickup and delivery."
          >
            <MotionCard>
              <Card>
                <CardHeader>
                  <CardTitle>Channel breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {data && data.channelMix.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex h-3 w-full overflow-hidden rounded-md">
                        {data.channelMix.map((c) => (
                          <div
                            key={c.key}
                            className={cn('h-full', CHANNEL_COLOR[c.key] ?? 'bg-muted-foreground')}
                            style={{ width: `${c.sharePct}%` }}
                            title={`${c.key} — ${c.sharePct.toFixed(1)}%`}
                          />
                        ))}
                      </div>
                      <ul className="divide-y divide-border/40">
                        {data.channelMix.map((c) => (
                          <li key={c.key} className="flex items-center gap-3 py-2 text-sm">
                            <span
                              className={cn(
                                'h-2.5 w-2.5 shrink-0 rounded-full',
                                CHANNEL_COLOR[c.key] ?? 'bg-muted-foreground',
                              )}
                            />
                            <span className="min-w-0 flex-1 truncate font-medium">{c.key}</span>
                            <span className="w-20 text-right font-mono tabular-nums">
                              {formatINRCompact(c.sales)}
                            </span>
                            <span className="w-12 text-right font-mono text-2xs text-muted-foreground">
                              {c.sharePct.toFixed(0)}%
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <EmptyState title={loading ? 'Loading…' : 'No channel data'} />
                  )}
                </CardContent>
              </Card>
            </MotionCard>
          </ReviewSection>

          {/* 5 — Product performance */}
          <ReviewSection
            step={5}
            title="Product performance"
            description="Top products and category contributions by revenue."
          >
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <MotionCard>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" /> Top products
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-3">
                    {menuItems.length > 0 ? (
                      <ul className="divide-y divide-border/40">
                        {menuItems.map((it, i) => (
                          <li key={it.itemId} className="flex items-center gap-3 py-2.5 text-sm">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent text-2xs font-semibold text-muted-foreground">
                              {i + 1}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">{it.name}</span>
                              {it.categoryName && (
                                <span className="block truncate text-2xs text-muted-foreground">
                                  {it.categoryName}
                                </span>
                              )}
                            </span>
                            <span className="hidden font-mono text-2xs text-muted-foreground sm:inline">
                              {formatNumberCompact(it.units)} units
                            </span>
                            <span className="w-20 text-right font-mono tabular-nums">
                              {formatINRCompact(it.revenue)}
                            </span>
                            <DeltaBadge deltaPct={it.metric.deltaPct} comparable={it.metric.comparable} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyState
                        icon={<Package className="h-5 w-5" />}
                        title={menu.isLoading ? 'Loading…' : 'No item sales for this period'}
                      />
                    )}
                  </CardContent>
                </Card>
              </MotionCard>

              <MotionCard delay={0.05}>
                <Card className="h-full">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Layers className="h-4 w-4 text-muted-foreground" /> Category contributions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-3">
                    {categories.length > 0 ? (
                      <ul className="divide-y divide-border/40">
                        {categories.slice(0, 10).map((c) => (
                          <li key={c.category} className="py-2 text-sm">
                            <div className="flex items-center gap-3">
                              <span className="min-w-0 flex-1 truncate font-medium">{c.category}</span>
                              <span className="font-mono text-2xs text-muted-foreground">
                                {formatNumberCompact(c.units)} units
                              </span>
                              <span className="w-20 text-right font-mono tabular-nums">
                                {formatINRCompact(c.revenue)}
                              </span>
                              <span className="w-10 text-right font-mono text-2xs text-muted-foreground">
                                {c.sharePct.toFixed(0)}%
                              </span>
                            </div>
                            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                              <div
                                className="h-full rounded-full bg-emerald-500"
                                style={{ width: `${Math.min(100, c.sharePct)}%` }}
                              />
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <EmptyState
                        icon={<Layers className="h-5 w-5" />}
                        title={menu.isLoading ? 'Loading…' : 'No category data for this period'}
                      />
                    )}
                  </CardContent>
                </Card>
              </MotionCard>
            </div>
          </ReviewSection>

          {/* 6 — Week-on-week trend */}
          <ReviewSection
            step={6}
            title="Week-on-week trend"
            description="Net sales by week, with online contribution and WoW growth."
          >
            {data && data.weekly.length >= 2 ? (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <MotionCard className="lg:col-span-2">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle>Net sales by week</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <BarChart
                        data={data.weekly.map((w) => ({ week: w.label, 'Net sales': w.net }))}
                        index="week"
                        categories={['Net sales']}
                        colors={['emerald']}
                        valueFormatter={formatINRCompact}
                        showLegend={false}
                        showGridLines={false}
                        className="h-72"
                      />
                    </CardContent>
                  </Card>
                </MotionCard>
                <MotionCard delay={0.05}>
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle>Weekly detail</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-3">
                      <ul className="divide-y divide-border/40">
                        {data.weekly.map((w) => (
                          <li key={w.label} className="flex items-center gap-2 py-2.5 text-sm">
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">{w.label}</span>
                              <span className="block text-2xs text-muted-foreground">
                                {formatINRCompact(w.net)} · {w.onlinePct.toFixed(0)}% online
                              </span>
                            </span>
                            <DeltaBadge deltaPct={w.wowPct ?? 0} comparable={w.wowPct != null} />
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </MotionCard>
              </div>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>Net sales by week</CardTitle>
                </CardHeader>
                <CardContent>
                  <EmptyState
                    icon={<CalendarDays className="h-5 w-5" />}
                    title="Pick a multi-week period"
                    description="Choose a month or a custom range spanning 2+ weeks to see the trend."
                  />
                </CardContent>
              </Card>
            )}
          </ReviewSection>

          {/* 7 — Sales by city */}
          <ReviewSection
            step={7}
            title="Sales by city"
            description="Net sales, gross and bills rolled up by city."
          >
            {data && data.cities.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {data.cities.map((c) => (
                  <MotionCard key={c.city}>
                    <Card className="overflow-hidden">
                      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 pb-2">
                        <CardTitle className="text-sm">{c.city}</CardTitle>
                        <MapPin className="h-4 w-4 text-muted-foreground/70" />
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="text-2xl font-semibold tracking-tight tabular-nums">
                          {formatINRCompact(c.net)}
                        </div>
                        <div className="flex gap-4 text-xs text-muted-foreground">
                          <span>Gross {formatINRCompact(c.gross)}</span>
                          <span>{formatNumberCompact(c.bills)} bills</span>
                        </div>
                        <div className="pt-1">
                          <DeltaBadge deltaPct={c.netMetric.deltaPct} comparable={c.netMetric.comparable} />
                        </div>
                      </CardContent>
                    </Card>
                  </MotionCard>
                ))}
              </div>
            ) : (
              <EmptyState title={loading ? 'Loading…' : 'No city data'} />
            )}
          </ReviewSection>

          {/* 8 — Executive summary */}
          <ReviewSection
            step={8}
            title="Executive summary"
            description="Auto-generated highlights from this period's POS data."
          >
            <MotionCard>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-muted-foreground" /> Highlights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {insights.length > 0 ? (
                    <ul className="space-y-2.5">
                      {insights.map((line, i) => (
                        <li key={i} className="flex gap-2.5 text-sm">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                          <span className="text-pretty">{line}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <EmptyState icon={<TrendingDown className="h-5 w-5" />} title="Not enough data yet" />
                  )}
                </CardContent>
              </Card>
            </MotionCard>
          </ReviewSection>
        </div>
      )}
    </div>
  );
}
